import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';


import {BasicPage} from 'src/components/basic-page';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {BASIC_VALIDATION_FAILED} from 'src/modules/create-account/constants';
import {SettingsType as SettingsTypes} from 'src/openapi/admin/models/SettingsType';

import Button from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';

import {ConferenceTabs} from '../conference-tabs';
import {CONFERENCE_SUB_TABS} from '../constants';

import {
    AdvertisementLogin,
    AdvertisementPopup,
    useAdvertisementImage,
    useAdvertisementLogin,
    useAdvertisementSettings,
    useValidateAdvertisement,
} from './advertisement-popup';
import {
    Banner,
    useBanner,
} from './banner';

export const SECTION_NAME = 'Conference Advertisement';

type Props = {
    accountId: number,
    conferenceId: number,
};

const Advertisement = ({accountId, conferenceId}: Props) => {
    const [busy, setBusy] = useState(false);
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {accountSubmit} = useEventTracking();
    const {hasAccess} = useAdminAccessRight();

    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'conference-advertisement-tab',
        onProceed: () => {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const {
        isLoading: isAdvertisementLoginLoading,
        loadingError: advertisementLoginLoadingError,
        isChanged: isAdvertisementLoginChanged,
        data: advertisementLogin,
        setData: setAdvertisementLogin,
        saveChanges: saveAdvertisementLogin,
        discardChanges: discardAdvertisementLogin,
    } = useAdvertisementLogin({conferenceId});

    const {
        isLoading: isWebAdvertisementLoading,
        loadingError: webAdvertisementLoadingError,
        isChanged: isWebAdvertisementChanged,
        data: webAdvertisement,
        setData: setWebAdvertisement,
        saveChanges: saveWebAdvertisement,
        discardChanges: discardWebAdvertisement,
    } = useAdvertisementSettings({
        conferenceId,
        settingsType: SettingsTypes.WEB,
    });

    const {
        isLoading: isWebImageLoading,
        loadingError: webImageLoadingError,
        isChanged: isWebImageChanged,
        data: webImage,
        setData: setWebImage,
        saveChanges: saveWebImage,
        discardChanges: discardWebImage,
    } = useAdvertisementImage({
        conferenceId,
        settingsType: SettingsTypes.WEB,
    });

    const {
        isLoading: isMobileAdvertisementLoading,
        loadingError: mobileAdvertisementLoadingError,
        isChanged: isMobileAdvertisementChanged,
        data: mobileAdvertisement,
        setData: setMobileAdvertisement,
        saveChanges: saveMobileAdvertisement,
        discardChanges: discardMobileAdvertisement,
    } = useAdvertisementSettings({
        conferenceId,
        settingsType: SettingsTypes.MOBILE,
    });

    const {
        isLoading: isMobileImageLoading,
        loadingError: mobileImageLoadingError,
        isChanged: isMobileImageChanged,
        data: mobileImage,
        setData: setMobileImage,
        saveChanges: saveMobileImage,
        discardChanges: discardMobileImage,
    } = useAdvertisementImage({
        conferenceId,
        settingsType: SettingsTypes.MOBILE,
    });

    const {
        isLoading: isWebBannerSettingsLoading,
        loadingError: webBannerSettingsLoadingError,
        isChanged: isWebBannerSettingsChanged,
        data: webBannerSettings,
        setData: setWebBannerSettings,
        saveChanges: saveWebBannerSettings,
        discardChanges: discardWebBannerSettings,
    } = useBanner({
        conferenceId,
        settingsType: SettingsTypes.WEB,
    });

    const {
        isLoading: isMobileBannerSettingsLoading,
        loadingError: mobileBannerSettingsLoadingError,
        isChanged: isMobileBannerSettingsChanged,
        data: mobileBannerSettings,
        setData: setMobileBannerSettings,
        saveChanges: saveMobileBannerSettings,
        discardChanges: discardMobileBannerSettings,
    } = useBanner({
        conferenceId,
        settingsType: SettingsTypes.MOBILE,
    });

    const {saveValidation} = useValidateAdvertisement();

    useEffect(() => {
        setIsChunkChanged([
            isAdvertisementLoginChanged,
            isWebImageChanged,
            isWebAdvertisementChanged,
            isMobileImageChanged,
            isMobileAdvertisementChanged,
            isWebBannerSettingsChanged,
            isMobileBannerSettingsChanged,
        ].some(Boolean));
    }, [
        setIsChunkChanged,
        isWebAdvertisementChanged, isMobileAdvertisementChanged, isAdvertisementLoginChanged,
        isWebImageChanged, isMobileImageChanged, isWebBannerSettingsChanged, isMobileBannerSettingsChanged,
    ]);

    const saveChanges = useCallback(async () => {
        accountSubmit({submit: true, place: SECTION_NAME});
        const calls = [];

        setBusy(true);
        if (isAdvertisementLoginChanged) {
            calls.push(await saveAdvertisementLogin());
        }

        if (isWebBannerSettingsChanged) {
            calls.push(await saveWebBannerSettings());
        }
        if (isMobileBannerSettingsChanged) {
            calls.push(await saveMobileBannerSettings());
        }

        const webValid = saveValidation({
            image: webImage,
            ...webAdvertisement,
        });

        const mobileValid = saveValidation({
            image: mobileImage,
            ...mobileAdvertisement,
        });

        if (isWebImageChanged || isWebAdvertisementChanged) {
            if (!webValid) {
                setErrorMessage(BASIC_VALIDATION_FAILED);
                setBusy(false);
                return;
            }

            if (isWebImageChanged && !isWebAdvertisementChanged) {
                calls.push(await saveWebImage());
            } else if (isWebAdvertisementChanged && !isWebImageChanged) {
                calls.push(await saveWebAdvertisement());
            } else if (webImage) {
                calls.push(await saveWebAdvertisement());
                calls.push(await saveWebImage());
            } else {
                calls.push(await saveWebImage());
                calls.push(await saveWebAdvertisement());
            }
        }

        if (isMobileImageChanged || isMobileAdvertisementChanged) {
            if (!mobileValid) {
                setErrorMessage(BASIC_VALIDATION_FAILED);
                setBusy(false);
                return;
            }

            if (isMobileImageChanged && !isMobileAdvertisementChanged) {
                calls.push(await saveMobileImage());
            } else if (isMobileAdvertisementChanged && !isMobileImageChanged) {
                calls.push(await saveMobileAdvertisement());
            } else if (mobileImage) {
                calls.push(await saveMobileAdvertisement());
                calls.push(await saveMobileImage());
            } else {
                calls.push(await saveMobileImage());
                calls.push(await saveMobileAdvertisement());
            }
        }
        setBusy(false);

        const errors = [...new Set(calls.filter(Boolean))];
        if (errors.length) {
            setErrorMessage(errors.join('\n'));
        } else {
            proceed();
        }
    }, [
        webImage,
        mobileImage,
        webAdvertisement,
        mobileAdvertisement,

        isAdvertisementLoginChanged,
        saveAdvertisementLogin,
        isWebAdvertisementChanged,
        saveWebAdvertisement,
        isMobileAdvertisementChanged,
        saveMobileAdvertisement,
        isWebImageChanged,
        saveWebImage,
        isMobileImageChanged,
        saveMobileImage,
        isWebBannerSettingsChanged,
        saveWebBannerSettings,
        isMobileBannerSettingsChanged,
        saveMobileBannerSettings,
        accountSubmit,
        proceed,
        saveValidation,
        setErrorMessage,
    ]);

    const discardChanges = useCallback(() => {
        discardAdvertisementLogin();
        discardWebAdvertisement();
        discardMobileAdvertisement();
        discardWebImage();
        discardMobileImage();
        discardWebBannerSettings();
        discardMobileBannerSettings();

        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
        accountSubmit({submit: false, place: SECTION_NAME});
    }, [
        accountSubmit, setSuccessMessage,
        discardWebAdvertisement, discardMobileAdvertisement, discardAdvertisementLogin,
        discardWebImage, discardMobileImage, discardWebBannerSettings, discardMobileBannerSettings,
    ]);

    const editable = useMemo(() => hasAccess(ACCESS_RIGHTS.CONFERENCE_EDIT), [hasAccess]);

    return (
        <>
            <ConferenceTabs accountId={accountId} activeSubTab={CONFERENCE_SUB_TABS.ADVERTISEMENT.name}/>
            <BasicPage
                title="Advertisement"
                buttonsBlock={editable ? (
                    <Group>
                        <Button variant="Type.SECONDARY" disabled={!isCurrentChunkChanged} onClick={discardChanges}>
                            Discard Changes
                        </Button>
                        <Button
                            busy={busy}
                            variant="Type.PRIMARY"
                            disabled={!isCurrentChunkChanged}
                            onClick={saveChanges}>
                            Save Changes
                        </Button>
                    </Group>
                ) : null}>
                <Item className="group-item pd-l">
                    <Group direction="v" gap="xl">
                        <FormSection headline="Banner">
                            <Group gap="xl" alignItems="start">
                                <Banner
                                    type={SettingsTypes.WEB}
                                    isLoading={isWebBannerSettingsLoading}
                                    loadingError={webBannerSettingsLoadingError}
                                    bannerSettings={webBannerSettings}
                                    setBannerSettings={setWebBannerSettings}
                                    readonly={!editable}
                                />
                                <Banner
                                    type={SettingsTypes.MOBILE}
                                    isLoading={isMobileBannerSettingsLoading}
                                    loadingError={mobileBannerSettingsLoadingError}
                                    bannerSettings={mobileBannerSettings}
                                    setBannerSettings={setMobileBannerSettings}
                                    readonly={!editable}
                                />
                            </Group>
                        </FormSection>
                        <FormSection headline="Advertisement Pop-up">
                            <Group direction="v">
                                <Group gap="xl">
                                    <AdvertisementPopup
                                        type={SettingsTypes.WEB}
                                        readonly={!editable}
                                        isLoading={isWebAdvertisementLoading || isWebImageLoading}
                                        loadingError={webAdvertisementLoadingError || webImageLoadingError}
                                        image={webImage}
                                        setImage={setWebImage}
                                        settings={webAdvertisement}
                                        setSettings={setWebAdvertisement}
                                    />
                                    <AdvertisementPopup
                                        type={SettingsTypes.MOBILE}
                                        readonly={!editable}
                                        isLoading={isMobileAdvertisementLoading || isMobileImageLoading}
                                        loadingError={mobileAdvertisementLoadingError || mobileImageLoadingError}
                                        image={mobileImage}
                                        setImage={setMobileImage}
                                        settings={mobileAdvertisement}
                                        setSettings={setMobileAdvertisement}
                                    />
                                </Group>
                                <AdvertisementLogin
                                    readonly={!editable}
                                    isLoading={isAdvertisementLoginLoading}
                                    loadingError={advertisementLoginLoadingError}
                                    data={advertisementLogin}
                                    setData={setAdvertisementLogin}
                                />
                            </Group>
                        </FormSection>
                    </Group>
                </Item>
            </BasicPage>
        </>
    );
};

export const ValidatedAdvertisementConfigurations = (props: Props) => (
    <ValidationContextProvider>
        <Advertisement {...props}/>
    </ValidationContextProvider>
);
